/**
 * enrichMay12VentasWithPropina.ts — Añade subtotal/servicio/propina al seed demo de ventas.
 *
 * Usado solo por may12CajaDemoSeed al exportar ventas. No importar en UI de producción.
 */

import type { Venta } from "../types/caja.types";
import { buildServicioPorMetodo } from "../utils/propinaAllocation";

const SERVICIO_PCT_BY_ID: Record<string, number> = {
  "may12-v-01": 15,
  "may12-v-13": 15,
  "may12-v-14": 15,
  "may12-v-15": 15,
  "may12-v-02": 10,
  "may12-v-03": 15,
  "may12-v-04": 10,
  "may12-v-05": 0,
  "may12-v-06": 15,
  "may12-v-07": 10,
  "may12-v-08": 20,
  "may12-v-09": 15,
  "may12-v-16": 15,
  "may12-v-10": 5,
  "may12-v-11": 15,
};

const subtotalFromProductos = (v: Venta): number =>
  v.productos.reduce((s, p) => s + p.cantidad * p.precioUnitario, 0);

function scaleMixtoBreakdown(
  v: Venta,
  prevTotal: number,
  nextTotal: number,
): Venta["detalleMetodoPago"] {
  const breakdown = v.detalleMetodoPago?.breakdown;
  if (!breakdown?.length || prevTotal <= 0) return v.detalleMetodoPago;

  let allocated = 0;
  const scaled = breakdown.map((b, i) => {
    const isLast = i === breakdown.length - 1;
    const amount = isLast
      ? nextTotal - allocated
      : Math.round((b.amount * nextTotal) / prevTotal);
    allocated += isLast ? amount : amount;
    return { ...b, amount };
  });
  return { breakdown: scaled };
}

export function enrichMay12VentaWithPropina(venta: Venta): Venta {
  const subtotal = subtotalFromProductos(venta);
  const servicioPct =
    venta.estado === "anulada" ? 0 : (SERVICIO_PCT_BY_ID[venta.id] ?? 10);

  if (servicioPct <= 0) {
    return {
      ...venta,
      subtotal,
      servicioPct: 0,
      servicioAmount: 0,
      servicioPorMetodo: undefined,
    };
  }

  const servicioAmount = Math.round((subtotal * servicioPct) / 100);
  const total = subtotal + servicioAmount;
  const prevTotal = venta.total;
  const detalleMetodoPago =
    venta.metodoPago === "mixto"
      ? scaleMixtoBreakdown(venta, prevTotal, total)
      : venta.detalleMetodoPago;

  const servicioPorMetodo = buildServicioPorMetodo({
    total,
    servicioAmount,
    metodoPago: venta.metodoPago,
    breakdown:
      venta.metodoPago === "mixto"
        ? detalleMetodoPago?.breakdown
        : [{ method: venta.metodoPago, amount: total }],
  });

  return {
    ...venta,
    subtotal,
    servicioPct,
    servicioAmount,
    total,
    detalleMetodoPago,
    servicioPorMetodo,
  };
}

export function enrichMay12VentasWithPropina(ventas: Venta[]): Venta[] {
  return ventas.map(enrichMay12VentaWithPropina);
}
